import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Zap, Target, GraduationCap, ArrowRight, ArrowLeft, BookOpen, Layers, Scale, ScrollText } from "lucide-react";
import TTSControls, { stripMarkdownForSpeech } from "@/components/TTSControls";

const iconMap = { Zap, Target, GraduationCap, Layers, Scale, ScrollText };

// Safe markdown renderer with Tailwind-styled output for the study kit
function renderStudyMarkdown(md) {
  if (!md) return "";
  let html = md;
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  html = html.replace(/^### (.*)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*)$/gm, "<h1>$1</h1>");
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  // Tables
  html = html.replace(/((?:^\|.*\|\s*\n)+)/gm, (block) => {
    const rows = block.trim().split("\n").map((r) => r.trim());
    if (rows.length < 2) return block;
    const header = rows[0].slice(1, -1).split("|").map((c) => c.trim());
    const body = rows.slice(2).map((r) => r.slice(1, -1).split("|").map((c) => c.trim()));
    const th = header.map((c) => `<th>${c}</th>`).join("");
    const bd = body.map((row) => `<tr>${row.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("");
    return `<table><thead><tr>${th}</tr></thead><tbody>${bd}</tbody></table>`;
  });
  // Numbered list items
  html = html.replace(/^(\d+)\. (.*)$/gm, "<li>$2</li>");
  html = html.replace(/^(?:- |\* )(.*)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]*?<\/li>)(?=(?:\n(?!<li>))|$)/g, (m) => `<ul>${m}</ul>`);
  html = html.split(/\n{2,}/).map((chunk) => {
    if (/^<(h\d|ul|ol|table|pre|blockquote)/.test(chunk.trim())) return chunk;
    return `<p>${chunk.replace(/\n/g, " ")}</p>`;
  }).join("\n");
  return html;
}

export function StudyKitIndex() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/study-kit");
      setDocs(data.docs);
      setLoading(false);
    })();
  }, []);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="study-kit-page">
        <div className="overline mb-3">Study Kit</div>
        <h1 className="font-serif text-5xl text-[var(--dw-navy)] mb-4 tracking-tight leading-tight">
          The reference library.
        </h1>
        <p className="text-neutral-700 leading-relaxed max-w-2xl mb-10">
          The complete reference library — cheat sheets, tactical exam strategy, the licensing pathway,
          the full 12-module SAFE 20 curriculum, a section-by-section SAFE Act deep dive, and the master
          UST study guide. Everything you need before Prometric.
        </p>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="dw-card h-56 animate-pulse" />)}</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {docs.map((d) => {
              const Icon = iconMap[d.icon] || BookOpen;
              return (
                <Link
                  key={d.slug}
                  to={`/study-kit/${d.slug}`}
                  data-testid={`study-doc-${d.slug}`}
                  className="dw-card group p-6 hover:border-[var(--dw-gold)] transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-[var(--dw-navy)]/15 mb-4 rounded-sm bg-[var(--dw-navy)]/[0.02]">
                    <Icon className="w-5 h-5 text-[var(--dw-navy)]" strokeWidth={1.5} />
                  </div>
                  <div className="font-serif text-xl text-[var(--dw-navy)] leading-tight mb-2">{d.title}</div>
                  <div className="text-sm text-neutral-600 leading-relaxed mb-4">{d.subtitle}</div>
                  <div className="text-xs font-medium text-[var(--dw-navy)] flex items-center gap-1 group-hover:gap-2 transition-all">
                    Open document <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export function StudyKitDoc() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/study-kit/${slug}`);
        setDoc(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <AppLayout><div className="p-12 text-neutral-500">Loading document…</div></AppLayout>;
  if (!doc) return <AppLayout><div className="p-12 text-neutral-500">Document not found.</div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="study-doc-page">
        <Link to="/study-kit" className="text-xs text-neutral-500 hover:text-[var(--dw-navy)] inline-flex items-center gap-1 mb-6" data-testid="back-to-study-kit">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All study documents
        </Link>

        <div className="overline mb-3">Study Kit</div>
        <h1 className="font-serif text-5xl text-[var(--dw-navy)] mb-4 tracking-tight leading-tight">{doc.title}</h1>
        <p className="text-neutral-600 mb-6 max-w-2xl">{doc.subtitle}</p>

        <div className="mb-8" data-testid="study-doc-tts">
          <TTSControls getText={() => stripMarkdownForSpeech(doc.content)} label={`Listen: ${doc.title}`} />
        </div>

        <article className="dw-prose" data-testid="study-doc-content" dangerouslySetInnerHTML={{ __html: renderStudyMarkdown(doc.content) }} />
      </div>
    </AppLayout>
  );
}
